import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ConfigProvider from '../config-provider';
import Icon from '../icon';
import Button from '../button';
import Dropdown from '../dropdown';
import Menu from '../menu';
import Input from '../input';
import Select from '../select';
import { KEYCODE, str, obj } from '../util';
import zhCN from '../locale/zh-cn.js';

const { Option } = Select;
const noop = () => {};

/**
 * Pagination
 */
class Pagination extends Component {
  static displayName = 'Pagination';

  static propTypes = {
    prefix: PropTypes.string,
    pure: PropTypes.bool,
    rtl: PropTypes.bool,
    className: PropTypes.string,
    /**
     * 自定义国际化文案对象
     */
    locale: PropTypes.object,
    /**
     * 分页组件类型
     */
    type: PropTypes.oneOf(['normal', 'simple']),
    /**
     * （受控）当前页码
     */
    current: PropTypes.number,
    /**
     * （非受控）初始页码
     */
    defaultCurrent: PropTypes.number,
    /**
     * 页码发生改变时的回调函数
     * @param {Number} current 改变后的页码数
     * @param {Object} e 点击事件对象
     */
    onChange: PropTypes.func,
    /**
     * 总记录数
     */
    total: PropTypes.number,
    /**
     * 总数的渲染函数
     * @param {Number} total 总数
     * @param {Array} range 当前数据在总数中的区间
     */
    totalRender: PropTypes.func,
    /**
     * 页码显示的数量，更多的使用...代替
     */
    pageShowCount: PropTypes.number,
    /**
     * 一页中的记录数
     */
    pageSize: PropTypes.number,
    /**
     * 每页显示选择器类型
     */
    pageSizeSelector: PropTypes.oneOf([false, 'dropdown']),
    /**
     * 每页显示选择器可选值
     */
    pageSizeList: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.number),
      PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.number,
        }),
      ),
    ]),
    /**
     * 每页显示记录数量改变时的回调函数
     * @param {Number} pageSize 改变后的每页显示记录数
     */
    onPageSizeChange: PropTypes.func,
    /**
     * 当分页数为1时，是否隐藏分页器
     */
    hideOnlyOnePage: PropTypes.bool,
    /**
     * type 设置为 normal 时，在页码数超过5页后，会显示跳转输入框与按钮，当设置 showJump 为 false 时，不再显示该跳转区域
     */
    showJump: PropTypes.bool,
    selectPopupContiner: PropTypes.func,
    /**
     * 弹层组件属性，透传给Popup
     */
    popupProps: PropTypes.object,
  };

  static defaultProps = {
    prefix: 'next-',
    pure: false,
    rtl: false,
    locale: zhCN.Pagination,
    type: 'normal',
    defaultCurrent: 1,
    onChange: noop,
    pageSize: 10,
    pageSizeSelector: false,
    pageSizeList: [5, 10, 20],
    onPageSizeChange: noop,
    total: 100,
    pageShowCount: 5,
    hideOnlyOnePage: false,
    showJump: true,
    selectPopupContiner: node => node.parentNode,
  };

  constructor(props, context) {
    super(props, context);
    const { current, defaultCurrent, total, pageSize } = props;
    this.state = {
      current: this.correctCurrent(current || defaultCurrent, total, pageSize),
      currentPageSize: pageSize,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { current, total, pageSize } = nextProps;

    const st = {};
    const newCurrent = this.correctCurrent(current || this.state.current, total, pageSize);
    if (this.state.current !== newCurrent) {
      st.current = newCurrent;
    }
    if (this.state.currentPageSize !== pageSize) {
      st.currentPageSize = pageSize;
    }

    if (Object.keys(st).length) {
      this.setState(st);
    }
  }

  correctCurrent = (currentPage, total, currentPageSize) => {
    const totalPage = this.getTotalPage(total, currentPageSize);
    return currentPage > totalPage ? totalPage : currentPage;
  }

  getTotalPage = (total, currentPageSize) => {
    const totalPage = Math.ceil(total / currentPageSize);
    return totalPage <= 0 ? 1 : totalPage;
  }

  onJump = (e) => {
    const { total } = this.props;
    const { current, currentPageSize } = this.state;
    const totalPage = this.getTotalPage(total, currentPageSize);
    const value = parseInt(this.inputValue, 10);
    if (typeof value === 'number' && value >= 1 && value <= totalPage && value !== current) {
      this.onPageItemClick(value, e);
    }
  }

  onPageItemClick = (page, e) => {
    if (!('current' in this.props)) {
      this.setState(
        {
          current: page,
        },
        () => {
          this.props.onChange(page, e);
        },
      );
    } else {
      this.props.onChange(page, e);
    }
  }

  onInputChange = (value) => {
    this.inputValue = value;
  }

  onSelectSize = (pageSize) => {
    const newState = {
      currentPageSize: pageSize,
    };

    const totalPage = this.getTotalPage(this.props.total, pageSize);
    if (this.state.current > totalPage) {
      newState.current = totalPage;
    }

    this.setState(newState);
    this.props.onPageSizeChange(pageSize);
  }

  renderPageTotal = () => {
    const { prefix, total, totalRender } = this.props;
    const { currentPageSize, current } = this.state;
    const range = [(current - 1) * currentPageSize + 1, current * currentPageSize];

    return <div className={`${prefix}pagination-total`}>{totalRender(total, range)}</div>;
  }

  renderPageItem = (index) => {
    const { prefix, total, pageSize, locale } = this.props;
    const { current } = this.state;

    const totalPage = this.getTotalPage(total, pageSize);
    const isCurrent = parseInt(index, 10) === current;
    const props = {
      className: cx({
        [`${prefix}pagination-item`]: true,
        [`${prefix}current`]: isCurrent,
      }),
      onClick: isCurrent ? noop : this.onPageItemClick.bind(this, index),
    };

    return (
      <div
        aria-label={str.template(locale.total, {
          current: index,
          total: totalPage,
        })}
        {...props}
        key={index}
      >
        {index}
      </div>
    );
  }

  renderPageFirst = (current) => {
    const { prefix, locale } = this.props;

    const isFirst = current <= 1;
    const props = {
      className: cx({
        [`${prefix}pagination-item`]: true,
        [`${prefix}pagination-item-disabled`]: isFirst,
        [`${prefix}prev`]: true,
      }),
      onClick: isFirst ? null : this.onPageItemClick.bind(this, current - 1),
    };

    return (
      <Icon
        type="chevron-left-s"
        {...props}
        aria-label={str.template(locale.labelPrev, { current })}
      />
    );
  }

  renderPageLast = (current, totalPage) => {
    const { prefix, locale } = this.props;

    const isLast = current >= totalPage;
    const props = {
      className: cx({
        [`${prefix}pagination-item`]: true,
        [`${prefix}pagination-item-disabled`]: isLast,
        [`${prefix}next`]: true,
      }),
      onClick: isLast ? null : this.onPageItemClick.bind(this, current + 1),
    };

    return (
      <Icon
        type="chevron-right-s"
        {...props}
        aria-label={str.template(locale.labelNext, { current })}
      />
    );
  }

  renderPageEllipsis = (idx) => {
    const { prefix } = this.props;

    return (
      <Icon
        type="more"
        className={`${prefix}pagination-ellipsis`}
        key={`ellipsis-${idx}`}
      />
    );
  }

  renderPageJump = () => {
    const { prefix, locale } = this.props;

    /* eslint-disable react/jsx-key */
    return [
      <span className={`${prefix}pagination-jump-text`}>{locale.goTo}</span>,
      <Input
        size="xs"
        className={`${prefix}pagination-jump-input`}
        type="text"
        aria-label={locale.inputAriaLabel}
        onChange={this.onInputChange}
        onKeyDown={e => {
          if (e.keyCode === KEYCODE.ENTER) {
            this.onJump(e);
          }
        }}
      />,
      <span className={`${prefix}pagination-jump-text`}>{locale.page}</span>,
    ];
    /* eslint-enable react/jsx-key */
  }

  renderPageDisplay = (current, totalPage) => {
    const { prefix } = this.props;
    const menu = [];
    for (let i = 1; i <= totalPage; i++) {
      menu.push(<Menu.Item key={i}>{i}</Menu.Item>);
    }
    return (
      <span className={`${prefix}pagination-display`}>
        <Dropdown
          trigger={<Button type="secondary">{`${current}/${totalPage}`}</Button>}
          triggerType="click"
        >
          <Menu
            style={{ maxHeight: 152, overflow: 'auto' }}
            selectMode="single"
            selectedKeys={`${current}`}
            onSelect={(selectedKeys) => { this.onPageItemClick(+selectedKeys[0]); }}
          >
            { menu }
          </Menu>
        </Dropdown>
      </span>
    );
  }

  renderPageList = (current, totalPage) => {
    const { prefix, pageShowCount } = this.props;
    const pages = [];

    if (totalPage <= pageShowCount) {
      for (let i = 1; i <= totalPage; i++) {
        pages.push(this.renderPageItem(i));
      }
    } else {
      // 除去第一页，最后一页以及当前页，剩下的页数
      const othersCount = pageShowCount - 3;
      const halfCount = parseInt(othersCount / 2, 10);
      let start;
      let end;

      pages.push(this.renderPageItem(1));

      start = current - halfCount;
      end = current + halfCount;
      if (start <= 1) {
        start = 2;
        end = start + othersCount;
      }
      if (start > 2) {
        pages.push(this.renderPageEllipsis(1));
      }
      if (end >= totalPage - 1) {
        end = totalPage - 1;
        start = totalPage - 1 - othersCount;
      }
      for (let j = start; j <= end; j++) {
        pages.push(this.renderPageItem(j));
      }
      if (end < totalPage - 1) {
        pages.push(this.renderPageEllipsis(2));
      }

      pages.push(this.renderPageItem(totalPage));
    }

    return <div className={`${prefix}pagination-list`}>{pages}</div>;
  }

  renderPageSizeSelector = () => {
    const { prefix, pageSizeSelector } = this.props;

    switch (pageSizeSelector) {
      case 'dropdown':
        return (
          <div className={`${prefix}pagination-size-selector`}>
            {this.renderPageSizeDropdown()}
          </div>
        );
      default:
        return null;
    }
  }

  renderPageSizeDropdown = () => {
    const { prefix, pageSizeList, selectPopupContiner, locale, popupProps } = this.props;
    const { currentPageSize } = this.state;

    return (
      <Select
        size="xs"
        className={`${prefix}pagination-size-selector-dropdown`}
        popupClassName={`${prefix}pagination-size-selector-popup`}
        popupContainer={selectPopupContiner}
        popupProps={popupProps}
        aria-label={locale.selectAriaLabel}
        autoWidth
        value={currentPageSize}
        valueRender={(item) => <span className={`${prefix}pagination-size-selector-dropdown-value-text`}>{`${item.value} ${locale.pageSize}`}</span>}
        onChange={this.onSelectSize}
      >
        {pageSizeList.map((item, index) => {
          let label;
          let pageSize;
          if (item.value) {
            // {label: '', value: 5}
            label = item.label;
            pageSize = item.value;
          } else {
            // number
            label = pageSize = item;
          }
          return (
            <Option key={index} value={pageSize}>
              {label}
            </Option>
          );
        })}
      </Select>
    );
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      prefix,
      pure,
      rtl,
      type,
      className,
      total,
      totalRender,
      pageSize,
      pageSizeSelector,
      pageSizeList,
      onPageSizeChange,
      hideOnlyOnePage,
      showJump,
      locale,
      current,
      defaultCurrent,
      pageShowCount,
      onChange,
      selectPopupContiner,
      popupProps,
      ...others
    } = this.props;
    /* eslint-enable */
    const { current: currentPage, currentPageSize } = this.state;
    const totalPage = this.getTotalPage(total, currentPageSize);
    const pageFirst = this.renderPageFirst(currentPage);
    const pageLast = this.renderPageLast(currentPage, totalPage);
    const sizeSelector = this.renderPageSizeSelector();

    const classes = cx({
      [`${prefix}pagination`]: true,
      [`${prefix}${type}`]: type,
      [`${prefix}hide`]: totalPage <= 1 && hideOnlyOnePage,
      [className]: !!className,
    });

    if (rtl) {
      others.dir = 'rtl';
    }

    const buildComponent = (...coms) => (
      <div className={classes} {...obj.pickAttrs(others)}>
        {totalRender ? this.renderPageTotal() : null}
        <div className={`${prefix}pagination-pages`}>
          {coms.map((com, index) => com && React.cloneElement(com, { key: index }))}
        </div>
      </div>
    );

    switch (type) {
      case 'simple': {
        const pageDisplay = this.renderPageDisplay(currentPage, totalPage);
        return buildComponent(pageFirst, pageDisplay, pageLast);
      }
      case 'normal': {
        const pageList = this.renderPageList(currentPage, totalPage);
        const pageJump =
          showJump && total > pageSize * pageShowCount
            ? this.renderPageJump(currentPage, totalPage)
            : [];
        return buildComponent(pageFirst, pageList, pageLast, sizeSelector, ...pageJump);
      }
      default:
        return null;
    }
  }
}

export default ConfigProvider.config(Pagination);
